import { CountrySelect, useCountries, countryOf } from "@/components/Country";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const CHAPTERS = ["The Story", "The Town Itself", "The Architecture", "Nature & Landscape", "Festivals & Traditions", "At the Table", "Day to Day", "Living Here: The Ratings", "Making the Move", "Beyond the Town", "The Expat Community", "Health, Vets & Pets", "Children: Schools, Sport & Activities", "Property: Buying & Ongoing Costs", "The Tax System Explained"];
const FAMILY = "Australian families with children";
const FAMILY_CHAPTERS = ["The Story", "The Town Itself", "The Architecture", "Outdoors with Children", "Festivals, Traditions & Family Life", "At the Table", "Schools & Childcare", "Family Life: The Ratings", "Moving with Children", "Beyond the Town", "The Expat Community", "Health, Vets & Pets", "Sport, Clubs & Things to Do for Kids", "Property: Buying & Ongoing Costs", "The Tax System Explained"];
const ROMAN = ["I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X", "XI", "XII", "XIII", "XIV", "XV"];

const Field = ({ id, label, hint, ...props }) => (
  <div className="space-y-2">
    <Label htmlFor={id} className="text-[#3B1B48]">{label}</Label>
    <Input id={id} data-testid={`new-guide-${id}-input`} className="h-12 bg-white rounded-none" {...props} />
    {hint && <p className="text-xs text-[#8C827A]">{hint}</p>}
  </div>
);

export default function NewGuide() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [nextAll, setNextAll] = useState({ std: {}, fam: {} });
  const countryList = useCountries();
  const [f, setF] = useState({ town: params.get("town") || "", region: params.get("region") || "", country: params.get("country") || "Italy", audience: params.get("family") ? FAMILY : "Australians", issue_number: "", eur_aud_rate: "1.65", family: !!params.get("family") });
  const next = f.family ? nextAll.fam : nextAll.std;
  useEffect(() => { if (next[f.country] && !f.issue_number) setF((x) => ({ ...x, issue_number: String(next[x.country]) })); }, [nextAll]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { const c = countryOf(countryList, f.country); if (c && f.eur_aud_rate === "1.65") setF((x) => ({ ...x, eur_aud_rate: String(c.aud_rate) })); }, [countryList]); // eslint-disable-line react-hooks/exhaustive-deps
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const setFamily = (on) => {
    const n = (on ? nextAll.fam : nextAll.std)[f.country];
    setF((x) => ({ ...x, family: on, audience: on ? FAMILY : x.audience === FAMILY ? "Australians" : x.audience, issue_number: n ? String(n) : "" }));
  };

  useEffect(() => {
    api.get("/stats").then((r) => setNextAll({ std: r.data.next_issue_by_country || {}, fam: r.data.next_family_issue_by_country || {} })).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/guides", { ...f, issue_number: Number(f.issue_number) || null, eur_aud_rate: Number(f.eur_aud_rate) || 1.65 });
      toast.success(`Research started for ${data.town}`);
      nav(`/guides/${data.id}`);
    } catch (err) {
      toast.error(errMsg(err));
      setBusy(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[420px_1fr] gap-12 rise">
      <aside className="bg-[#3B1B48] text-[#FAF7F2] p-10 lg:sticky lg:top-28 self-start">
        <p className="eyebrow mb-4">What the studio produces</p>
        <h2 className="font-display text-3xl leading-tight mb-8">A complete issue, from one town name.</h2>
        <ol className="space-y-2.5">
          {(f.family ? FAMILY_CHAPTERS : CHAPTERS).map((c, i) => (
            <li key={c} className="flex gap-4 text-sm text-[#EFE6F3]"><span className="font-display text-[#C59B27] w-8">{ROMAN[i]}.</span>{c}</li>
          ))}
        </ol>
        <div className="border-t border-[#C59B27]/40 mt-8 pt-6 text-sm text-[#EFE6F3]/90 space-y-1.5">
          <p>+ Typeset A5 PDF guide</p><p>+ Offline interactive toolkit (HTML)</p><p>+ Store listing & product package</p>
        </div>
      </aside>

      <form onSubmit={submit} className="max-w-2xl" data-testid="new-guide-form">
        <p className="eyebrow mb-3">New issue</p>
        <h1 className="font-display text-4xl sm:text-5xl text-[#3B1B48] mb-4">Which town next?</h1>
        <p className="text-[#8C827A] mb-12">Research is drawn from Wikipedia (English and the country's language), Wikivoyage and Wikidata, then written by Claude to the house style. Takes about 3–5 minutes.</p>
        <div className="space-y-8">
          <Field id="town" label="Town name" required placeholder="e.g. Spello, Todi, Orvieto" value={f.town} onChange={set("town")} />
          <div className="grid sm:grid-cols-2 gap-6">
            <Field id="region" label="Region" placeholder="e.g. Umbria" hint="Helps disambiguate towns with the same name" value={f.region} onChange={set("region")} />
            <div className="space-y-2">
              <Label htmlFor="country" className="text-[#3B1B48]">Country · series</Label>
              <CountrySelect testId="new-guide-country-select" value={f.country} onChange={(v) => setF((x) => ({ ...x, country: v, issue_number: next[v] ? String(next[v]) : "", eur_aud_rate: String(countryOf(countryList, v)?.aud_rate || 1.65) }))} className="w-full h-12" />
              <p className="text-xs text-[#8C827A]">Each country is its own series with its own issue numbers and colours.</p>
            </div>
          </div>
          <label data-testid="new-guide-family-toggle" className={`flex gap-3 p-4 border cursor-pointer transition-colors ${f.family ? "border-[#3B1B48] bg-[#FAF7F2]" : "border-[#E2D9CC] hover:bg-[#FAF7F2]"}`}>
            <input type="checkbox" checked={f.family} onChange={(e) => setFamily(e.target.checked)} className="mt-1 accent-[#3B1B48] w-4 h-4" data-testid="new-guide-family-checkbox" />
            <span><span className="block font-display text-lg text-[#3B1B48]">Families with children edition</span>
              <span className="text-xs text-[#8C827A]">Its own series ({countryOf(countryList, f.country)?.series || "Small Towns"} for Families) with its own issue numbers. Chapters focus on schools, childcare, children's healthcare, outdoors with kids and moving with children.</span></span>
          </label>
          <div className="grid sm:grid-cols-3 gap-6">
            <Field id="audience" label="Audience" value={f.audience} onChange={set("audience")} />
            <Field id="issue" label="Issue no." type="number" min="1" value={f.issue_number} onChange={set("issue_number")} />
            <Field id="rate" label={`1 ${countryOf(countryList, f.country)?.currency || "EUR"} = AUD`} type="number" step="0.01" value={f.eur_aud_rate} onChange={set("eur_aud_rate")} />
          </div>
          <Button data-testid="new-guide-submit-button" disabled={busy || f.town.trim().length < 2} className="h-14 px-8 rounded-none text-base">
            <Sparkles className="w-4 h-4 mr-2" />{busy ? "Starting research…" : "Research & build the guide"}
          </Button>
        </div>
      </form>
    </div>
  );
}
